import React from "react";
import { Calendar, MapPin, User } from "lucide-react";
import { COLOR } from "../../constants/colors";
import { motion } from "framer-motion";

interface ConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  onSignInClick: () => void;
  selection: { day: string; time: string; dateLabel: string } | null;
  isSignedIn: boolean;
  userName?: string;
  userLinkedIn?: string;
}

export default function ConfirmationModal({
  open,
  onClose,
  onConfirm,
  onSignInClick,
  selection,
  isSignedIn,
  userName = "John Doe",
  userLinkedIn = "https://www.linkedin.com/in/johndoe",
}: ConfirmationModalProps) {
  if (!open || !selection) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-3xl shadow-xl w-full max-w-md relative"
        style={{ 
          maxHeight: "90vh", 
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <style>{`
          .bg-white.rounded-3xl::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        <button
          className="absolute top-4 right-4 text-2xl font-bold hover:opacity-70 transition"
          onClick={onClose}
          aria-label="Close"
          style={{ color: COLOR.subtext }}
        >
          ×
        </button>

        <div className="p-6">
          <h2
            className="text-2xl font-extrabold mb-4"
            style={{ color: COLOR.text }}
          >
            Confirm your visit
          </h2>

          <div
            className="mb-4 p-4 rounded-2xl"
            style={{
              backgroundColor: COLOR.bg,
              border: `2px solid ${COLOR.border}`,
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="h-5 w-5" style={{ color: COLOR.main }} />
              <p className="font-extrabold" style={{ color: COLOR.text }}>
                When
              </p>
            </div>
            <p className="text-sm font-bold" style={{ color: COLOR.text }}>
              {selection.dateLabel}
            </p>
            <p className="text-sm font-bold" style={{ color: COLOR.main }}>
              {selection.time}
            </p>
          </div>

          <div
            className="mb-4 p-4 rounded-2xl"
            style={{
              backgroundColor: COLOR.bg,
              border: `2px solid ${COLOR.border}`,
            }}
          >
            <div className="flex items-center gap-2 mb-2">
              <MapPin className="h-5 w-5" style={{ color: COLOR.main }} />
              <p className="font-extrabold" style={{ color: COLOR.text }}>
                Where
              </p>
            </div>
            <p className="text-sm font-bold" style={{ color: COLOR.text }}>
              Belltown, Seattle, WA
            </p>
            <p className="text-xs mt-1" style={{ color: COLOR.subtext }}>
              Exact address shared after confirmation
            </p>
          </div>

          {isSignedIn ? (
            <div
              className="mb-4 p-4 rounded-2xl"
              style={{
                backgroundColor: COLOR.bg,
                border: `2px solid ${COLOR.border}`,
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <User className="h-5 w-5" style={{ color: COLOR.main }} />
                <a
                  href={userLinkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 group"
                >
                  <p className="font-extrabold text-[#332626] group-hover:text-[#0077B5] transition">
                    {userName}
                  </p>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    className="transition flex-shrink-0"
                  >
                    <path
                      d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
                      fill="#0077B5"
                      className="transition"
                    />
                  </svg>
                </a>
              </div>
              <p className="text-xs" style={{ color: COLOR.subtext }}>
                We'll share your profile once you confirm your visit.
              </p>
            </div>
          ) : (
            <div
              className="mb-6 p-4 rounded-2xl"
              style={{
                backgroundColor: "#E8F4FF",
                border: `2px solid #B3DBFF`,
              }}
            >
              <p
                className="font-extrabold mb-2 text-sm"
                style={{ color: COLOR.text }}
              >
                Sign in to reserve your spot
              </p>
              <p className="text-xs mb-3" style={{ color: COLOR.text }}>
                Using LinkedIn builds trust and confidence in our community.
              </p>
              <button
                onClick={onSignInClick}
                className="w-full py-3 px-4 rounded-xl font-extrabold text-sm transition hover:opacity-90 flex items-center justify-center gap-2"
                style={{
                  backgroundColor: "#0077B5",
                  color: "white",
                  border: "2px solid #005983",
                }}
              >
                <svg width="20" height="20" viewBox="0 0 32 32" fill="none">
                  <path
                    d="M27 27h-4.5v-6.5c0-1.5-.5-2.5-2-2.5-1.5 0-2 1-2 2.5V27H14V13h4.5v2h.1c.6-1.1 2-2.2 4.1-2.2 3.1 0 4.3 2 4.3 5.1V27zM7.5 11.5c-1.5 0-2.5-1-2.5-2.5s1-2.5 2.5-2.5 2.5 1 2.5 2.5-1 2.5-2.5 2.5zM9.5 27H5.5V13h4V27z"
                    fill="white"
                  />
                </svg>
                Continue with LinkedIn
              </button>
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-3 px-4 rounded-xl font-extrabold text-sm transition hover:opacity-70"
              style={{
                backgroundColor: COLOR.bg,
                border: `2px solid ${COLOR.border}`,
                color: COLOR.text,
              }}
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={!isSignedIn}
              className="flex-1 py-3 px-4 rounded-xl font-extrabold text-sm transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                backgroundColor: COLOR.main,
                border: `2px solid ${COLOR.mainBorder}`,
                color: "white",
              }}
            >
              Confirm Visit
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
